import { useCallback, useEffect, useState } from 'react';
import { useAuthStore } from '../stores/authStore';
import {
  DEFAULT_CURRENCY,
  SUPPORTED_CURRENCIES,
  isKnownCurrency,
} from '../lib/constants/currencyConstants';

/**
 * M4.c (AB#927): Dashboard "Display Currency" — per-user kullanıcı tercihi.
 *
 * Storage hâlâ ham (multi-currency) tutulur; bu hook yalnızca **gösterim** katmanını
 * etkiler. Seçim `localStorage`'da persist edilir (anahtar: <c>display-currency</c>),
 * tenant değişiminde de korunur — kişisel tercih global.
 *
 * Fallback zinciri:
 *   1. <c>localStorage.getItem('display-currency')</c> (SUPPORTED_CURRENCIES içindeyse).
 *   2. Auth user'ın `localizationSettings.currency` değeri (= TenantOperationsDefaults).
 *   3. <c>DEFAULT_CURRENCY</c> ('TRY').
 *
 * Aynı sekmede iki ayrı bileşen kullanırsa `storage` event'i tetiklenmediği için
 * `useState` + custom event ile in-tab sync sağlanır (`display-currency-change`).
 */

const STORAGE_KEY = 'display-currency';
const IN_TAB_EVENT = 'display-currency-change';

function readPersisted(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw && isKnownCurrency(raw) ? raw.toUpperCase() : null;
  } catch {
    return null;
  }
}

export interface UseDisplayCurrencyResult {
  /** Şu an aktif gösterim para birimi. */
  displayCurrency: string;
  /** Kullanıcı seçimi — null = tenant default fallback. */
  setDisplayCurrency: (code: string | null) => void;
  /** UI dropdown için: SUPPORTED_CURRENCIES listesi (read-only). */
  supportedCurrencies: readonly string[];
}

export function useDisplayCurrency(): UseDisplayCurrencyResult {
  const tenantDefault = useAuthStore(
    (s) => s.user?.localizationSettings?.currency,
  );
  const [persisted, setPersisted] = useState<string | null>(() => readPersisted());

  useEffect(() => {
    function onChange() {
      setPersisted(readPersisted());
    }
    window.addEventListener('storage', onChange);
    window.addEventListener(IN_TAB_EVENT, onChange);
    return () => {
      window.removeEventListener('storage', onChange);
      window.removeEventListener(IN_TAB_EVENT, onChange);
    };
  }, []);

  const setDisplayCurrency = useCallback((code: string | null) => {
    if (typeof window === 'undefined') return;
    try {
      if (code && isKnownCurrency(code)) {
        window.localStorage.setItem(STORAGE_KEY, code.toUpperCase());
      } else {
        window.localStorage.removeItem(STORAGE_KEY);
      }
      window.dispatchEvent(new Event(IN_TAB_EVENT));
    } catch {
      // localStorage may be unavailable (private mode / disabled); ignore silently.
    }
  }, []);

  const displayCurrency =
    persisted ??
    (tenantDefault && isKnownCurrency(tenantDefault) ? tenantDefault.toUpperCase() : null) ??
    DEFAULT_CURRENCY;

  return {
    displayCurrency,
    setDisplayCurrency,
    supportedCurrencies: SUPPORTED_CURRENCIES,
  };
}
